<template>
  <div class="surgeon-report-container">
    <div class="report-header-wrapper">
      <div class="report-header d-flex justify-content-between align-items-center">
        <div class="header-left">
          <h3 class="report-title">
            <i class="el-icon-s-data mr-2"></i>
            Surgeon Statistics Report
          </h3>
        </div>
        <div class="export-buttons">
          <el-button 
            type="primary" 
            size="small" 
            icon="el-icon-download" 
            :disabled="!surgeon_statistics.length"
            @click="exportToCSV"
          >
            Export to CSV
          </el-button>
        </div>
      </div>
      <div class="row my-3 align-items-end">
        <div class="col-auto">
          <span>Select Date(s)</span>
          <br />
          <el-select v-model="dateFilterOption">
            <el-option :value="0" :label="'All'">All</el-option>
            <el-option :value="1" :label="'Custom Date'">Custom Date</el-option>
          </el-select>
        </div>
        <div v-if="dateFilterOption" class="col-auto">
          <span>Select Date Range</span>
          <v-md-date-range-picker
            :presets="dateRangePresets"
            :start-date="dateRange.start_date"
            :end-date="dateRange.end_date"
            @change="handleChange"
            style="width:100% !important"
          ></v-md-date-range-picker>
        </div>
        <div class="col mt-2">
          <el-button type="primary" @click="getSurgeonReport"
            >Get Report</el-button
          >
        </div>
      </div>
      <p class="report-subtitle" v-if="surgeon_statistics.length">
            Showing statistics for {{ surgeon_statistics.length }} surgeons
          </p>
    </div>

    <div v-if="!surgeon_statistics.length" class="text-center no-data-message">
      <i class="el-icon-warning-outline mr-2"></i>No Statistics found
    </div>

    <div v-else class="surgeon-cards">
      <el-card 
        v-for="(surgeon, index) in surgeon_statistics" 
        :key="index" 
        class="surgeon-card" 
        shadow="hover"
      >
        <div class="surgeon-header">
          <h4 class="surgeon-name mb-1">{{ surgeon.surgeon_name }}</h4>
        </div>

        <el-divider class="compact-divider"></el-divider>

        <div class="stats-container">
          <div class="row stats-row">
            <div class="col-md-4 stat-item">
              <div class="stat-label">Total Spays (Female)</div>
              <div class="stat-value">{{ surgeon.spay_female }}</div>
            </div>
            <div class="col-md-4 stat-item">
              <div class="stat-label">Total Castrations (Male)</div>
              <div class="stat-value">{{ surgeon.castration_male }}</div>
            </div>
            <div class="col-md-4 stat-item">
              <div class="stat-label">Total Surgeries</div>
              <div class="stat-value">{{ surgeon.total_surgeries }}</div>
            </div>
          </div>

          <div class="row stats-row mt-2">
            <div class="col-md-4 stat-item">
              <div class="stat-label">Avg Time</div>
              <div class="stat-value time-value">
                <i class="el-icon-time mr-1"></i>
                {{ surgeon.average_time_mins }} mins
              </div>
            </div>
            <div class="col-md-4 stat-item">
              <div class="stat-label">Total Duration</div>
              <div class="stat-value time-value">
                <i class="el-icon-time mr-1"></i>
                {{ surgeon.total_duration_mins }} mins
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { Card, Button, Tag, Divider, Message } from 'element-ui';
import apiService from '../../../../services/api.service';
import dateRangeDefaultPresetsMixin from '../../../../mixins/DateRangeDefaultPresetsMixin';

export default {
  components: {
    [Card.name]: Card,
    [Button.name]: Button,
    [Tag.name]: Tag,
    [Divider.name]: Divider
  },
  props: {
    projectId: {
      type: String,
      required: true
    }
  },
  data() {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      surgeon_statistics: [],
      dateRange: {
        start_date: firstDay.toLocaleDateString("en-US"),
        end_date: lastDay.toLocaleDateString("en-US"),
      },
      dateFilterOption: 0,
      dateRangePresets: [],
    };
  },
  mixins: [dateRangeDefaultPresetsMixin],
  methods: {
    async getSurgeonReport() {
      const vm = this;
      const loading = vm.$loading({
        lock: true,
        text: 'Loading statistics...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });
      try {
        const payload = this.dateFilterOption ? this.dateRange : {};
        const apiResponse = await apiService.getSurgeonStatistics(vm.projectId, payload);
        const { response } = apiResponse;
        if (response.error && response.error.error_code === 0) {
          vm.surgeon_statistics = response.data.vets;
          // Sort surgeons by total surgeries in descending order
          vm.surgeon_statistics.sort((a, b) => b.total_surgeries - a.total_surgeries);
        } else {
          Message.error('Failed to load surgeon statistics');
        }
      } catch (error) {
        console.error('Error fetching surgeon statistics:', error);
        Message.error('An error occurred while fetching surgeon statistics');
      } finally {
        loading.close();
      }
    },
    
    exportToCSV() {
      if (!this.surgeon_statistics.length) {
        Message.warning('No data to export');
        return;
      }
      
      try {
        // CSV header
        const headers = [
          'Surgeon Name',
          'Total Spays (Female)',
          'Total Castrations (Male)',
          'Total Surgeries',
          'Avg Time (mins)',
          'Total Time Duration (mins)'
        ];
        
        // CSV rows
        const rows = this.surgeon_statistics.map(surgeon => [
          surgeon.surgeon_name,
          surgeon.spay_female,
          surgeon.castration_male,
          surgeon.total_surgeries,
          surgeon.average_time_mins,
          surgeon.total_duration_mins
        ]);
        
        // Combine headers and rows
        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.join(','))
        ].join('\n');
        
        // Create a Blob with the CSV content
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        
        // Create a download link and trigger the download
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', `surgeon_statistics_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        Message.success('Export successful');
      } catch (error) {
        console.error('Error exporting data:', error);
        Message.error('Failed to export data');
      }
    },
    handleChange(values) {
      this.dateRange.start_date = this.formatDate(values[0]._d);
      this.dateRange.end_date = this.formatDate(values[1]._d);
    },
    formatDate(date) {
      return new Date(date).toISOString().split("T")[0];
    }
  },
  mounted() {
    this.getSurgeonReport();
    this.dateRangePresets = this.getDateRangePresets();
  }
};
</script>

<style scoped>
.surgeon-report-container {
  padding: 20px;
}

.report-header-wrapper {
  margin-bottom: 20px;
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 15px;
}

.report-title {
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
}

.report-subtitle {
  color: #909399;
  font-size: 14px;
  margin-bottom: 0;
}

.no-data-message {
  padding: 40px;
  font-size: 16px;
  color: #909399;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.surgeon-card {
  transition: all 0.3s ease;
  border-radius: 8px;
  overflow: hidden;
  padding: 5px 10px;
  margin-bottom: 15px;
}

.surgeon-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
}


.surgeon-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-top: 0;
}

.compact-divider {
  margin: 5px 0;
}

.stats-container {
  margin-top: 5px;
}

.stats-row {
  display: flex;
  flex-wrap: wrap;
}

.stat-item {
  padding: 5px 10px;
  border-radius: 4px;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 5px;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.time-value {
  color: #409eff;
}

@media (max-width: 768px) {
  .stat-item {
    margin-bottom: 15px;
  }
  
  .report-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .export-buttons {
    margin-top: 10px;
  }
}
</style>
